<script setup lang="ts">
/**
 * Filter drawer for the fiat-transactions directory.
 *
 * Holds every filter that is NOT the treasury account picker — the
 * treasury account is the lens the operator already filters by inline
 * above the table, so it stays a quick filter. Everything else
 * (date range, investor name/email, reference type, direction, reason,
 * amount min/max, sort order) lives here.
 *
 * Pinned "Apply filters" CTA at the bottom per the canonical
 * drawer-chrome pattern in `.claude/rules/ui-kit-theme.md`.
 */
import { reactive, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import AppDrawer from '@uiKit/elements/Drawer.vue'
import DatePicker from '@uiKit/elements/DatePicker.vue'
import Input from '@uiKit/elements/Input.vue'
import Select from '@uiKit/elements/Select.vue'
import DrawerHeader from '@uiKit/components/DrawerHeader.vue'
import FormField from '@uiKit/components/FormField.vue'
import type { SelectOption } from '@shared/types'
import {
  FIAT_TRANSACTION_REASON,
  FIAT_TRANSACTION_REFERENCE_TYPE,
  FIAT_TRANSACTION_STATUS,
  FIAT_TRANSACTION_TYPE,
  type FiatTransactionReason,
  type FiatTransactionReferenceType,
  type FiatTransactionStatus,
  type FiatTransactionType,
  type FiatTransactionsSortOrder,
} from '../types/fiatTransactionsTypes'
import {
  referenceTypeLabel,
  transactionReasonLabel,
  transactionStatusLabel,
  transactionTypeLabel,
} from '../utils/fiatTransactionLabels'

export interface FiatTransactionsDrawerFilterValues {
  fromDate: Date | null
  toDate: Date | null
  investorName: string
  investorEmail: string
  status: FiatTransactionStatus | null
  referenceType: FiatTransactionReferenceType | null
  transactionType: FiatTransactionType | null
  transactionReason: FiatTransactionReason | null
  amountMin: string
  amountMax: string
  sortOrder: FiatTransactionsSortOrder
}

interface Props {
  visible: boolean
  values: FiatTransactionsDrawerFilterValues
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'apply', values: FiatTransactionsDrawerFilterValues): void
  (e: 'reset'): void
}>()

const draft = reactive<FiatTransactionsDrawerFilterValues>({
  fromDate: props.values.fromDate,
  toDate: props.values.toDate,
  investorName: props.values.investorName,
  investorEmail: props.values.investorEmail,
  status: props.values.status,
  referenceType: props.values.referenceType,
  transactionType: props.values.transactionType,
  transactionReason: props.values.transactionReason,
  amountMin: props.values.amountMin,
  amountMax: props.values.amountMax,
  sortOrder: props.values.sortOrder,
})

watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) return
    draft.fromDate = props.values.fromDate
    draft.toDate = props.values.toDate
    draft.investorName = props.values.investorName
    draft.investorEmail = props.values.investorEmail
    draft.status = props.values.status
    draft.referenceType = props.values.referenceType
    draft.transactionType = props.values.transactionType
    draft.transactionReason = props.values.transactionReason
    draft.amountMin = props.values.amountMin
    draft.amountMax = props.values.amountMax
    draft.sortOrder = props.values.sortOrder
  },
)

const statusOptions = [
  { label: 'All', value: null },
  ...Object.values(FIAT_TRANSACTION_STATUS).map((value) => ({
    label: transactionStatusLabel(value),
    value,
  })),
]

const referenceTypeOptions = [
  { label: 'All', value: null },
  ...Object.values(FIAT_TRANSACTION_REFERENCE_TYPE).map((value) => ({
    label: referenceTypeLabel(value),
    value,
  })),
]

const transactionTypeOptions = [
  { label: 'All', value: null },
  ...Object.values(FIAT_TRANSACTION_TYPE).map((value) => ({
    label: transactionTypeLabel(value),
    value,
  })),
]

const transactionReasonOptions = [
  { label: 'All', value: null },
  ...Object.values(FIAT_TRANSACTION_REASON).map((value) => ({
    label: transactionReasonLabel(value),
    value,
  })),
]

const sortOrderOptions: SelectOption<FiatTransactionsSortOrder>[] = [
  { label: 'Newest first', value: 'desc' },
  { label: 'Oldest first', value: 'asc' },
]

function handleApply() {
  emit('apply', {
    fromDate: draft.fromDate,
    toDate: draft.toDate,
    investorName: draft.investorName.trim(),
    investorEmail: draft.investorEmail.trim(),
    status: draft.status,
    referenceType: draft.referenceType,
    transactionType: draft.transactionType,
    transactionReason: draft.transactionReason,
    amountMin: draft.amountMin.trim(),
    amountMax: draft.amountMax.trim(),
    sortOrder: draft.sortOrder,
  })
  emit('update:visible', false)
}

function handleReset() {
  draft.fromDate = null
  draft.toDate = null
  draft.investorName = ''
  draft.investorEmail = ''
  draft.status = null
  draft.referenceType = null
  draft.transactionType = null
  draft.transactionReason = null
  draft.amountMin = ''
  draft.amountMax = ''
  draft.sortOrder = 'desc'
  emit('reset')
}

function handleClose() {
  emit('update:visible', false)
}
</script>

<template>
  <AppDrawer
    :model-value="visible"
    position="right"
    width="440px"
    :show-close-icon="false"
    @update:model-value="(v) => !v && handleClose()"
  >
    <template #header>
      <DrawerHeader
        title="Filter transactions"
        subtitle="Narrow the ledger view"
        @close="handleClose"
      >
        <template #extras>
          <Button label="Reset" variant="text" color="secondary" @click="handleReset" />
        </template>
      </DrawerHeader>
    </template>

    <Box flex direction="col" class="drawer-body">
      <Box flex direction="col" gap="4" class="drawer-body__content">
        <Box flex direction="row" gap="3">
          <FormField label="From" class="date-field">
            <DatePicker v-model="draft.fromDate" placeholder="DD/MM/YYYY" date-format="dd/mm/yy" />
          </FormField>
          <FormField label="To" class="date-field">
            <DatePicker v-model="draft.toDate" placeholder="DD/MM/YYYY" date-format="dd/mm/yy" />
          </FormField>
        </Box>
        <FormField label="Investor name">
          <Input v-model="draft.investorName" placeholder="Name contains…" />
        </FormField>
        <FormField label="Investor email">
          <Input v-model="draft.investorEmail" placeholder="Email contains…" />
        </FormField>
        <FormField label="Status">
          <Select
            v-model="draft.status"
            :options="statusOptions"
            option-label="label"
            option-value="value"
            placeholder="All"
          />
        </FormField>
        <FormField label="Reference type">
          <Select
            v-model="draft.referenceType"
            :options="referenceTypeOptions"
            option-label="label"
            option-value="value"
            placeholder="All"
          />
        </FormField>
        <FormField label="Direction">
          <Select
            v-model="draft.transactionType"
            :options="transactionTypeOptions"
            option-label="label"
            option-value="value"
            placeholder="All"
          />
        </FormField>
        <FormField label="Reason">
          <Select
            v-model="draft.transactionReason"
            :options="transactionReasonOptions"
            option-label="label"
            option-value="value"
            placeholder="All"
          />
        </FormField>
        <Box flex direction="row" gap="3">
          <FormField label="Amount min" class="amount-field">
            <Input v-model="draft.amountMin" placeholder="0.00" />
          </FormField>
          <FormField label="Amount max" class="amount-field">
            <Input v-model="draft.amountMax" placeholder="10000.00" />
          </FormField>
        </Box>
        <FormField label="Sort order">
          <Select
            v-model="draft.sortOrder"
            :options="sortOrderOptions"
            option-label="label"
            option-value="value"
          />
        </FormField>
      </Box>

      <Box class="drawer-body__actions">
        <Button label="Apply filters" color="primary" block size="large" @click="handleApply" />
      </Box>
    </Box>
  </AppDrawer>
</template>

<style scoped>
.drawer-body {
  min-height: 100%;
  padding: 1.25rem 1.5rem;
}

.drawer-body__content {
  flex: 1;
}

.drawer-body__actions {
  margin-top: auto;
  padding-top: 1rem;
}

.amount-field,
.date-field {
  flex: 1 1 0;
}

@media (max-width: 992px) {
  .drawer-body {
    padding: 1rem;
  }
}
</style>
